import React, { useState } from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
import { products } from '../mock';
import { Link } from 'react-router-dom';

const ProductsPage = () => {
  const [expandedProduct, setExpandedProduct] = useState(null);

  return (
    <div data-testid="products-page" className="bg-white pt-24 sm:pt-28 pb-16 sm:pb-24">
      {/* Page Header */}
      <section className="py-10 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
            Our Products
          </p>
          <h1 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
            Glass Solutions
          </h1>
          <p className="font-roboto text-slate-500 mt-3 sm:mt-4 text-sm sm:text-base max-w-xl mx-auto">
            High-performance glass solutions engineered for excellence
          </p>
        </div>
      </section>

      {/* Products */}
      <section className="py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-8 sm:space-y-12">
          {products.map((product, index) => (
            <div
              key={product.id}
              data-testid={`product-detail-${product.id}`}
              className="border border-slate-200 overflow-hidden hover:border-brand/20 transition-all duration-300"
            >
              {/* Product Header */}
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image - always on top on mobile */}
                <div className={`relative h-56 sm:h-72 md:h-96 overflow-hidden ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>

                {/* Info */}
                <div className={`p-5 sm:p-8 md:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <h2 className="font-rajdhani text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-3 sm:mb-4">
                    {product.name}
                  </h2>
                  <p className="font-roboto text-slate-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
                    {product.description}
                  </p>
                  <button
                    onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                    data-testid={`toggle-specs-${product.id}`}
                    className="inline-flex items-center gap-2 font-rajdhani text-brand font-semibold text-sm hover:text-brand-dark transition-colors"
                  >
                    <span>{expandedProduct === product.id ? 'Hide' : 'View'} Technical Specifications</span>
                    <ChevronDown
                      size={20}
                      className={`transition-transform duration-300 ${expandedProduct === product.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
              </div>

              {/* Expandable Specs */}
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  expandedProduct === product.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-5 sm:p-8 md:p-12 border-t border-slate-200 bg-slate-50">
                  <div className="grid sm:grid-cols-2 gap-6 sm:gap-10">
                    <div>
                      <h3 className="font-rajdhani text-lg sm:text-xl font-bold text-slate-800 mb-4 sm:mb-5 flex items-center gap-2">
                        <span className="w-1 h-5 bg-brand"></span>
                        Technical Specifications
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {product.technicalSpecs.map((spec, i) => (
                          <li key={i} className="flex items-start gap-2 sm:gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-brand mt-2 flex-shrink-0"></div>
                            <span className="font-roboto text-slate-600 text-xs sm:text-sm">{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-rajdhani text-lg sm:text-xl font-bold text-slate-800 mb-4 sm:mb-5 flex items-center gap-2">
                        <span className="w-1 h-5 bg-brand"></span>
                        Key Features
                      </h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {product.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 sm:gap-3">
                            <CheckCircle2 size={16} className="text-brand flex-shrink-0 mt-0.5" />
                            <span className="font-roboto text-slate-600 text-xs sm:text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="p-8 sm:p-12 bg-slate-50 border border-slate-200">
            <h3 className="font-rajdhani text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
              Need Custom Specifications?
            </h3>
            <p className="font-roboto text-slate-500 text-sm sm:text-base mb-6 sm:mb-8">
              Our engineering team can develop glass solutions tailored to your exact requirements
            </p>
            <Link
              to="/contact"
              data-testid="products-contact-cta"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-rajdhani font-semibold px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base transition-colors"
            >
              Contact Our Experts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
